import { EntityStatusHighlightColors } from '@global-dax-ad-platform/dax-types';
import { theme } from '@global-engineering-shared/gweb-material-theme-next';

export const colorMapping: Partial<Record<EntityStatusHighlightColors, string>> = {
  success: theme.palette.success.main,
  error: theme.palette.error.main,
  warning: theme.palette.warning.main,
  primary: theme.palette.primary.main,
  cancelled: theme.palette.grey[500],
};

export enum LiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum DraftStatusByTable {
  SYNCING = 'sync',
  READY = 'ready',
  ERROR = 'error',
}

export enum DraftStatus {
  SYNCING = 'SYNCING',
  PENDING_SYNC = 'PENDING_SYNC',
  READY = 'READY',
  INCOMPLETE = 'INCOMPLETE',
  INVALID = 'INVALID',
  ERROR = 'ERROR',
  NOT_FOUND = 'NOT_FOUND',
}

export enum LinkedAssetFrameStatus {
  ADDED = 'Added',
  UPDATED = 'Updated',
  UNCHANGED = 'Unchanged',
  REMOVED = 'Removed',
}
export const statusColours: Record<keyof typeof LiveStatus, EntityStatusHighlightColors> = {
  ACTIVE: 'success',
  INACTIVE: 'cancelled',
  ARCHIVED: 'cancelled',
};

export const draftStatusColours: Record<keyof typeof DraftStatus, EntityStatusHighlightColors> = {
  SYNCING: 'primary',
  PENDING_SYNC: 'primary',
  READY: 'success',
  INCOMPLETE: 'warning',
  INVALID: 'warning',
  ERROR: 'error',
  NOT_FOUND: 'error',
};
export const linkedAssetFrameStatusColours: Record<
  keyof typeof LinkedAssetFrameStatus,
  EntityStatusHighlightColors
> = {
  ADDED: 'success',
  UPDATED: 'primary',
  UNCHANGED: 'cancelled',
  REMOVED: 'error',
};
export const statusLabel = (status: LiveStatus | DraftStatus | LinkedAssetFrameStatus): string => {
  switch (status) {
    case LiveStatus.ACTIVE:
      return 'Active';
    case LiveStatus.INACTIVE:
      return 'Inactive';
    case LiveStatus.ARCHIVED:
      return 'Archived';
    case DraftStatus.READY:
      return 'Ready';
    case DraftStatus.SYNCING:
    case DraftStatus.PENDING_SYNC:
      return 'Syncing';
    case DraftStatus.INCOMPLETE:
      return 'Incomplete';
    case DraftStatus.INVALID:
      return 'Invalid';
    case DraftStatus.ERROR:
      return 'Error';
    case DraftStatus.NOT_FOUND:
      return 'Not Found';
    case LinkedAssetFrameStatus.ADDED:
    case LinkedAssetFrameStatus.UPDATED:
    case LinkedAssetFrameStatus.UNCHANGED:
    case LinkedAssetFrameStatus.REMOVED:
      return status;
    default:
      return '';
  }
};

export const statusColour = (
  status: LiveStatus | DraftStatus | LinkedAssetFrameStatus
): EntityStatusHighlightColors => {
  switch (status) {
    case LiveStatus.ACTIVE:
      return statusColours.ACTIVE;
    case LiveStatus.INACTIVE:
      return statusColours.INACTIVE;
    case LiveStatus.ARCHIVED:
      return statusColours.ARCHIVED;
    case DraftStatus.READY:
      return draftStatusColours.READY;
    case DraftStatus.SYNCING:
      return draftStatusColours.SYNCING;
    case DraftStatus.PENDING_SYNC:
      return draftStatusColours.PENDING_SYNC;
    case DraftStatus.INCOMPLETE:
      return draftStatusColours.INCOMPLETE;
    case DraftStatus.INVALID:
      return draftStatusColours.INVALID;
    case DraftStatus.ERROR:
      return draftStatusColours.ERROR;
    case DraftStatus.NOT_FOUND:
      return draftStatusColours.NOT_FOUND;
    case LinkedAssetFrameStatus.ADDED:
      return linkedAssetFrameStatusColours.ADDED;
    case LinkedAssetFrameStatus.UPDATED:
      return linkedAssetFrameStatusColours.UPDATED;
    case LinkedAssetFrameStatus.UNCHANGED:
      return linkedAssetFrameStatusColours.UNCHANGED;
    case LinkedAssetFrameStatus.REMOVED:
      return linkedAssetFrameStatusColours.REMOVED;
    default:
      return 'error';
  }
};

export const isStatusError = (status: DraftStatus, size: number | undefined) => {
  return (
    (status === DraftStatus.ERROR ||
      status === DraftStatus.INCOMPLETE ||
      status === DraftStatus.INVALID ||
      status === DraftStatus.NOT_FOUND) &&
    size != undefined &&
    size > 0
  );
};
